/**
 * Scoring and the target scale. Settled in the build spec §3 — do not re-derive.
 *
 * Internal scale is 0–100. Display is 0–10 with one decimal (value / 10).
 */

/** Distance ≤ 3.5 → 4 points. */
export const BULLSEYE = 3.5;
/** Distance ≤ 8 → 3 points. */
export const MID = 8;
/** Distance ≤ 13 → 2 points. Beyond that, 0. */
export const OUTER = 13;

/**
 * Points a guess earns. A purely random guess scores ~26% of the time and
 * bullseyes ~7% — forgiving enough for a party, tight enough that a real clue
 * beats a shrug.
 */
export function pointsFor(guess: number, target: number): number {
  const d = Math.abs(guess - target);
  if (d <= BULLSEYE) return 4;
  if (d <= MID) return 3;
  if (d <= OUTER) return 2;
  return 0;
}

/** Clamp to the playable 0–100 range. Also the server-side guess clamp (§9). */
export function clamp100(v: number): number {
  return v < 0 ? 0 : v > 100 ? 100 : v;
}

/**
 * Quantise to the displayed resolution — integers on the internal scale, which
 * is one decimal on the 0–10 dial.
 *
 * Scoring runs on quantised values so the reveal can never contradict its own
 * arithmetic. Unquantised, the target and the guess round independently and
 * 2.4% of scored guesses print a distance that lands on the wrong side of a
 * band: `Target 4.1 / you said 2.8` scored as a miss. The reveal is the one
 * screen designed to be argued over, so it is the one screen that must not lie.
 * Calibration is unaffected — a random guess still scores 26.6% of the time.
 */
export function quantise(v: number): number {
  return Math.round(clamp100(v));
}

/**
 * Target generation, clamped away from the outer edges: a target at 0.2 is
 * unguessable by clue and produces a dead round.
 */
export function randomTarget(rand: () => number = Math.random): number {
  return quantise(8 + rand() * 84);
}

/** 63.7 → "6.4". The display scale. */
export function dial10(v: number): string {
  return (v / 10).toFixed(1);
}

/**
 * Score display: one decimal, trailing ".0" dropped. The giver earns the exact
 * average of the guessers' points — not rounded up — so a vague clue visibly
 * costs the giver something.
 */
export function fmtScore(n: number): string {
  return (Math.round(n * 10) / 10).toFixed(1).replace(/\.0$/, '');
}

/**
 * The giver's points: the exact mean of the guessers' points, or 0 if nobody
 * guessed. Deliberately NOT rounded — rounding here and then summing loses a
 * tenth per round: a giver who earns 2.333 twice must total 4.7, not 4.6.
 * Rounding happens once, at display, in `fmtScore`.
 */
export function giverPointsFor(guesserPoints: number[]): number {
  if (guesserPoints.length === 0) return 0;
  const total = guesserPoints.reduce((a, b) => a + b, 0);
  return total / guesserPoints.length;
}

/** Fisher–Yates. Returns a new array; never mutates the input. */
export function shuffle<T>(input: readonly T[], rand: () => number = Math.random): T[] {
  const a = input.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/** "Emory" → "EM", "Ada L" → "AL". Used for the dial's guess markers. */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * Initials for a whole room, disambiguated. Emory and Emma are both "EM", and
 * a reveal that labels two pills identically is a reveal that lies.
 */
export function initialsFor(names: string[]): string[] {
  const out = names.map(initials);
  const seen = new Map<string, number[]>();
  out.forEach((ini, i) => seen.set(ini, [...(seen.get(ini) ?? []), i]));
  for (const [, group] of seen) {
    if (group.length < 2) continue;
    for (const i of group) {
      const source = names[i].replace(/\s+/g, '');
      out[i] = (source.slice(0, 3) || out[i]).toUpperCase();
    }
    // Still colliding (same first three letters): fall back to a numeral.
    const still = new Map<string, number[]>();
    group.forEach((i) => still.set(out[i], [...(still.get(out[i]) ?? []), i]));
    for (const [, dupes] of still) {
      if (dupes.length < 2) continue;
      dupes.forEach((i, n) => (out[i] = `${out[i].slice(0, 2)}${n + 1}`));
    }
  }
  return out;
}

/** "1 round" / "3 rounds". Small, but the lobby is the first screen most players see. */
export function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? '' : 's'}`;
}
